/**
 * Section 1 — Upload / Data Source
 *
 * Full-width hero when no data is loaded.
 * Compact inline strip after a dataset is active.
 */
import React, { useState, useRef, useCallback } from 'react';
import { Upload, Link, Loader2, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';

const UploadSection = ({ onUpload, onImportSheet, loading, error, compact = false }) => {
  const [dragOver, setDragOver] = useState(false);
  const [sheetsUrl, setSheetsUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localError, setLocalError] = useState('');
  const fileInputRef = useRef(null);

  const displayError = error || localError;

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!['.csv', '.xlsx', '.xls'].includes(ext)) {
      setLocalError('Please upload a CSV or XLSX file.');
      return;
    }
    setLocalError('');
    setUploadProgress(30);
    await onUpload(file);
    setUploadProgress(0);
  }, [onUpload]);

  const handleGoogleImport = useCallback(async () => {
    if (!sheetsUrl.trim()) { setLocalError('Enter a Google Sheets URL.'); return; }
    if (!sheetsUrl.includes('docs.google.com/spreadsheets')) {
      setLocalError('That doesn\u2019t look like a valid Google Sheets URL.'); return;
    }
    setLocalError('');
    setUploadProgress(30);
    await onImportSheet(sheetsUrl);
    setSheetsUrl('');
    setUploadProgress(0);
  }, [sheetsUrl, onImportSheet]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  /* ---- Compact variant (after data loaded) ---- */
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3" data-testid="upload-section-compact">
        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={loading} className="gap-1.5">
          <Upload className="w-3.5 h-3.5" /> Upload New File
        </Button>
        <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="file-input" />
        <div className="flex items-center gap-2">
          <Input placeholder="Paste Google Sheets URL…" value={sheetsUrl} onChange={(e) => setSheetsUrl(e.target.value)} className="w-64 h-8 text-sm" data-testid="google-sheets-url-input" />
          <Button size="sm" variant="outline" onClick={handleGoogleImport} disabled={loading} data-testid="google-sheets-import-button">
            <Link className="w-3.5 h-3.5" />
          </Button>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>
    );
  }

  /* ---- Hero variant (empty state) ---- */
  return (
    <section className="py-6 md:py-10 hero-accent-bg" data-testid="upload-section">
      <div className="text-center mb-8">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-foreground mb-3">AI Multi-Sheet Analyst</h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
          Upload your spreadsheet or paste a Google Sheets link to get instant analysis,
          auto-generated charts, and AI-powered Q&A on your data.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
        {/* File upload */}
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <div
              className={`upload-dropzone p-8 text-center cursor-pointer ${dragOver ? 'dragover' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !loading && fileInputRef.current?.click()}
              data-testid="file-dropzone"
            >
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="file-input" />
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                    <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
                    <p className="text-sm text-muted-foreground">Processing file…</p>
                    {uploadProgress > 0 && <Progress value={uploadProgress} className="w-48 mx-auto" />}
                  </motion.div>
                ) : (
                  <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Drop your file here or click to browse</p>
                      <p className="text-xs text-muted-foreground mt-1">Supports CSV, XLSX (including multi-sheet)</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </CardContent>
        </Card>

        {/* Google Sheets */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Link className="w-5 h-5 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Import from Google Sheets</h3>
            </div>
            <p className="text-xs text-muted-foreground">Paste the URL of a publicly shared Google Sheet.</p>
            <Input placeholder="https://docs.google.com/spreadsheets/d/…" value={sheetsUrl} onChange={(e) => setSheetsUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleGoogleImport()} disabled={loading} data-testid="google-sheets-url-input" />
            <Button onClick={handleGoogleImport} disabled={loading || !sheetsUrl.trim()} className="w-full gap-2" data-testid="google-sheets-import-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />} Import Sheet
            </Button>
          </CardContent>
        </Card>

        {displayError && (
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />{displayError}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setLocalError(''); fileInputRef.current?.click(); }}
                className="shrink-0 text-xs border-destructive/30 text-destructive hover:bg-destructive/10"
              >
                Retry
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UploadSection;
